import type {
  HostedCheckoutReturnState,
  PaymentHistoryStatusPresentation,
  PaymentHistoryStatusTone,
} from './types';

const normalizeText = (value: unknown): string => String(value ?? '').trim().toLowerCase();

const makeStatusClassName = (tone: PaymentHistoryStatusTone): string => {
  switch (tone) {
    case 'success':
      return 'border border-[#28E2A7]/30 bg-[#28E2A7]/10 text-[#0F7B5A]';
    case 'warning':
      return 'border border-[#F59E0B]/30 bg-[#FFFBEB] text-[#92400E]';
    case 'danger':
      return 'border border-[#DC2626]/30 bg-[#FEF2F2] text-[#991B1B]';
    default:
      return 'border border-[#D1D5DB] bg-[#F9FAFB] text-[#4B5563]';
  }
};

export const readHostedCheckoutReturnState = (
  searchParamsLike: string | URLSearchParams | undefined | null,
): HostedCheckoutReturnState => {
  if (!searchParamsLike) {
    return null;
  }
  const searchParams = typeof searchParamsLike === 'string'
    ? new URLSearchParams(searchParamsLike.startsWith('?') ? searchParamsLike : `?${searchParamsLike}`)
    : searchParamsLike;
  const checkout = normalizeText(searchParams.get('checkout'));
  if (checkout === 'success') {
    return 'success';
  }
  if (checkout === 'cancel') {
    return 'cancel';
  }
  return null;
};

export const getPaymentHistoryStatusPresentation = (
  rawStatus: unknown,
): PaymentHistoryStatusPresentation => {
  const normalized = normalizeText(rawStatus).replace(/\./g, ' ').replace(/_/g, ' ');

  if (!normalized || normalized === 'processed' || normalized === 'paid' || normalized === 'active') {
    return {
      label: normalized === 'active' ? 'Active' : 'Paid',
      tone: 'success',
      className: makeStatusClassName('success'),
    };
  }
  if (
    normalized === 'pending'
    || normalized === 'recommendation submitted'
    || normalized === 'processing'
    || normalized === 'pending moderation'
    || normalized === 'requires action'
  ) {
    return {
      label: normalized.replace(/\b\w/g, (segment) => segment.toUpperCase()),
      tone: 'warning',
      className: makeStatusClassName('warning'),
    };
  }
  if (
    normalized === 'failed'
    || normalized === 'recommendation voided'
    || normalized === 'refunded'
    || normalized === 'disputed'
    || normalized === 'cancelled'
    || normalized === 'charge dispute created'
  ) {
    return {
      label: normalized.replace(/\b\w/g, (segment) => segment.toUpperCase()),
      tone: 'danger',
      className: makeStatusClassName('danger'),
    };
  }
  return {
    label: normalized.replace(/\b\w/g, (segment) => segment.toUpperCase()),
    tone: 'neutral',
    className: makeStatusClassName('neutral'),
  };
};
